// Layout shell for signed-in pages. Sticky top bar with brand + nav +
// user info, then main content area. Auth pages use AuthShell instead.

import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Bell, Calendar, LogOut, Plug, Server, User as UserIcon, Users, Workflow } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import { useOpenIncidents } from '../../hooks/useOpenIncidents';
import { OpenIncidentsBanner } from '../incidents/OpenIncidentsBanner';
import { Brand } from './Brand';
import { Badge } from './Badge';
import { Button } from './Button';

export function AppShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { role } = useOrg();
  const { triggered } = useOpenIncidents();
  return (
    <div className="ds-app">
      <header className="ds-app__header">
        <div className="ds-app__header-left">
          <Link to="/" style={{ color: 'inherit' }}>
            <Brand />
          </Link>
          <nav className="ds-app__nav" aria-label="Primary">
            <NavLink
              to="/incidents"
              className={({ isActive }) =>
                `ds-app__nav-link${isActive ? ' ds-app__nav-link--active' : ''}`
              }
            >
              <Bell size={14} strokeWidth={1.75} />
              Incidents
              {triggered > 0 && (
                <span className="ds-app__nav-badge" aria-label={`${triggered} unacknowledged`}>
                  {triggered}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/services"
              className={({ isActive }) =>
                `ds-app__nav-link${isActive ? ' ds-app__nav-link--active' : ''}`
              }
            >
              <Server size={14} strokeWidth={1.75} />
              Services
            </NavLink>
            {role === 'admin' && (
              <>
                <NavLink
                  to="/integrations"
                  className={({ isActive }) =>
                    `ds-app__nav-link${isActive ? ' ds-app__nav-link--active' : ''}`
                  }
                >
                  <Plug size={14} strokeWidth={1.75} />
                  Integrations
                </NavLink>
                <NavLink
                  to="/escalation-policies"
                  className={({ isActive }) =>
                    `ds-app__nav-link${isActive ? ' ds-app__nav-link--active' : ''}`
                  }
                >
                  <Workflow size={14} strokeWidth={1.75} />
                  Escalation
                </NavLink>
                <NavLink
                  to="/schedules"
                  className={({ isActive }) =>
                    `ds-app__nav-link${isActive ? ' ds-app__nav-link--active' : ''}`
                  }
                >
                  <Calendar size={14} strokeWidth={1.75} />
                  On-call
                </NavLink>
                <NavLink
                  to="/teams"
                  className={({ isActive }) =>
                    `ds-app__nav-link${isActive ? ' ds-app__nav-link--active' : ''}`
                  }
                >
                  <Users size={14} strokeWidth={1.75} />
                  Teams
                </NavLink>
                <NavLink
                  to="/settings/team"
                  end
                  className={({ isActive }) =>
                    `ds-app__nav-link${isActive ? ' ds-app__nav-link--active' : ''}`
                  }
                >
                  <Users size={14} strokeWidth={1.75} />
                  Members
                </NavLink>
              </>
            )}
            <NavLink
              to="/settings/profile"
              end
              className={({ isActive }) =>
                `ds-app__nav-link${isActive ? ' ds-app__nav-link--active' : ''}`
              }
            >
              <UserIcon size={14} strokeWidth={1.75} />
              Profile
            </NavLink>
          </nav>
        </div>
        <div className="ds-app__header-right">
          {user?.email && <span className="ds-mono">{user.email}</span>}
          {role && (
            <Badge tone={role === 'admin' ? 'accent' : 'neutral'} dot>
              {role}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<LogOut size={14} strokeWidth={1.75} />}
            onClick={() => signOut(auth).catch(() => undefined)}
          >
            Sign out
          </Button>
        </div>
      </header>
      <OpenIncidentsBanner />
      <main className="ds-app__main">{children}</main>
    </div>
  );
}
